import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { Component } from 'react';
import partial from 'react-utils/partial';
import Controllable from '../decorators/Controllable';
import H4 from '../elements/headings/H4';
import H7 from '../elements/headings/H7';
import { rgba } from '../core/Color';
import Small from '../elements/Small';
import SyntheticEvent from '../core/SyntheticEvent';
import UIButtonWrapper from '../layout/UIButtonWrapper';
import UIClickable from '../button/UIClickable';
import UIImage from '../image/UIImage';
import UINextButton from '../button/UINextButton';
import UIPreviousButton from '../button/UIPreviousButton';
import { BUTTON_RADIUS, BUTTON_SM_WIDTH, GRID_BREAKPOINT_SMALL } from 'HubStyleTokens/sizes';
import { CALYPSO, CALYPSO_MEDIUM } from 'HubStyleTokens/colors';
import styled from 'styled-components';
import { toPx } from '../utils/Styles';
import { uniqueId } from '../utils/underscore';
import { getComponentPropType } from '../utils/propTypes/componentProp';

const preloadImages = steps => {
  if (steps) {
    steps.forEach(step => {
      if (step && step.image) {
        const img = new Image();
        img.src = step.image;
      }
    });
  }
};

const DEFAULT_MAX_WIDTH = 826; // width + gutters

const GUTTER_SIZE = 20;
const MAX_THUMBNAILS = 5;
const THUMBNAIL_ASPECT_RATIO = 0.6909090909;
/* based on 76/110 spec */

const THUMBNAIL_GAP = 24;
const FLEX_FALLBACK_SPACE = parseInt(BUTTON_SM_WIDTH, 10) + GUTTER_SIZE;
const STACKED_LAYOUT = `
  'header header    header  '
  '.      steps     .       '
  'prev   contents  next    '
  '.      callout   .       '
  '.      footer    .       '
`;
const RESPONSIVE_LAYOUT = `
  'header     header      header  '
  'steps      steps       steps   '
  'contents   contents    contents'
  'prev       footer      next    '
  'callout    callout     callout '
`;
const CarouselWrapper = styled.section.withConfig({
  displayName: "UICarousel__CarouselWrapper",
  componentId: "sc-1swdb2g-0"
})(["display:flex;flex-wrap:wrap;grid-column-gap:", ";grid-template-areas:", ";grid-template-columns:auto 1fr auto;max-width:", ";padding:", ";padding-top:0;position:relative;> *{flex:1 1 100%;}@supports (display:grid){display:grid;}@media only screen and (max-width:", "){grid-template-areas:", ";grid-template-columns:auto 1fr auto;&& > *{margin-right:0;}}"], toPx(GUTTER_SIZE), STACKED_LAYOUT, props => toPx(props.maxWidth), toPx(GUTTER_SIZE), GRID_BREAKPOINT_SMALL, RESPONSIVE_LAYOUT);
const CarouselHeader = styled.div.withConfig({
  displayName: "UICarousel__CarouselHeader",
  componentId: "sc-1swdb2g-1"
})(["grid-area:header;margin-bottom:24px;text-align:center;@supports (display:grid){margin-right:0;}"]);
const CarouselImage = styled(UIImage).withConfig({
  displayName: "UICarousel__CarouselImage",
  componentId: "sc-1swdb2g-2"
})(["margin:0 auto;"]);
const CarouselTitle = styled(H4).withConfig({
  displayName: "UICarousel__CarouselTitle",
  componentId: "sc-1swdb2g-3"
})(["margin-bottom:0;"]);
const CarouselSubtitle = styled(H7).withConfig({
  displayName: "UICarousel__CarouselSubtitle",
  componentId: "sc-1swdb2g-4"
})(["margin-bottom:12px;"]);
const CarouselCallout = styled.aside.withConfig({
  displayName: "UICarousel__CarouselCallout",
  componentId: "sc-1swdb2g-5"
})(["grid-area:callout;margin-top:", ";margin-left:", ";margin-right:", ";text-align:center;@supports (display:grid){margin-left:0;margin-right:0;}@media only screen and (max-width:", "){margin-top:", ";padding-left:0;}"], toPx(GUTTER_SIZE), toPx(FLEX_FALLBACK_SPACE), toPx(FLEX_FALLBACK_SPACE), GRID_BREAKPOINT_SMALL, toPx(GUTTER_SIZE));
const CarouselContent = styled.section.withConfig({
  displayName: "UICarousel__CarouselContent",
  componentId: "sc-1swdb2g-6"
})(["flex-basis:calc(100% - ", ");grid-area:contents;align-self:center;"], toPx(FLEX_FALLBACK_SPACE * 2));
const CarouselContentInner = styled.div.withConfig({
  displayName: "UICarousel__CarouselContentInner",
  componentId: "sc-1swdb2g-7"
})([">:last-child{margin-bottom:0;}"]);
const CarouselNext = styled.div.withConfig({
  displayName: "UICarousel__CarouselNext",
  componentId: "sc-1swdb2g-8"
})(["align-self:center;flex-basis:", ";grid-area:next;text-align:right;"], toPx(FLEX_FALLBACK_SPACE));
const CarouselPrevious = styled.div.withConfig({
  displayName: "UICarousel__CarouselPrevious",
  componentId: "sc-1swdb2g-9"
})(["align-self:center;flex-basis:", ";grid-area:prev;"], toPx(FLEX_FALLBACK_SPACE));
const CarouselFooter = styled.footer.withConfig({
  displayName: "UICarousel__CarouselFooter",
  componentId: "sc-1swdb2g-10"
})(["grid-area:footer;margin-top:", ";padding:0 ", ";@supports (display:grid){margin-right:0;padding:0;}"], toPx(GUTTER_SIZE), toPx(FLEX_FALLBACK_SPACE));
const CarouselCurrent = styled(Small).withConfig({
  displayName: "UICarousel__CarouselCurrent",
  componentId: "sc-1swdb2g-11"
})(["display:block;margin:0 8px;text-align:center;"]);
const CarouselThumbnail = styled(UIClickable).withConfig({
  displayName: "UICarousel__CarouselThumbnail",
  componentId: "sc-1swdb2g-12"
})(["flex:0 1;flex-basis:calc(20% - ", ");border-radius:", ";box-shadow:", ";"], toPx(THUMBNAIL_GAP), BUTTON_RADIUS, props => props['aria-current'] ? `
        0 0 0 2px ${CALYPSO},
        0 0 12px 0 ${rgba(CALYPSO, 0.5)}
      ` : `0 0 0 1px ${CALYPSO_MEDIUM}`);
const AspectRatioBox = styled.div.withConfig({
  displayName: "UICarousel__AspectRatioBox",
  componentId: "sc-1swdb2g-13"
})(["height:0;overflow:hidden;padding-top:calc(100% * ", ");position:relative;"], THUMBNAIL_ASPECT_RATIO);
const CarouselThumbnailImage = styled(UIImage).withConfig({
  displayName: "UICarousel__CarouselThumbnailImage",
  componentId: "sc-1swdb2g-14"
})(["height:auto;left:50%;min-height:100%;min-width:100%;object-fit:cover;position:absolute;top:50%;transform:translateX(-50%) translateY(-50%);width:auto;"]);
const USE_OPTIONS = ['max', 'lite'];

const isValidStepIndex = (stepIndex, steps) => {
  if (!stepIndex || !steps) {
    return false;
  }

  return !(stepIndex < 0 || stepIndex > steps.length - 1);
};

const getMaxIndex = arr => arr.length - 1;

class UICarousel extends Component {
  constructor(...args) {
    super(...args);

    this.handleClickPreviousStep = () => {
      const {
        allowStepLoop,
        stepIndex,
        steps
      } = this.props;
      const computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      let newIndex;

      if (typeof computedStepIndex === 'number') {
        if (allowStepLoop && computedStepIndex === 0) {
          newIndex = getMaxIndex(steps);
        } else {
          newIndex = computedStepIndex - 1;
        }

        if (newIndex >= 0) {
          this.handleChange(newIndex);
        }
      }
    };

    this.handleClickNextStep = () => {
      const {
        allowStepLoop,
        stepIndex,
        steps
      } = this.props;
      const computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      let newIndex;

      if (typeof computedStepIndex === 'number') {
        if (allowStepLoop && getMaxIndex(steps) === computedStepIndex) {
          newIndex = 0;
        } else {
          newIndex = computedStepIndex + 1;
        }

        if (newIndex <= getMaxIndex(steps)) {
          this.handleChange(newIndex);
        }
      }
    };

    this.handleChange = newIndex => {
      if (this.props.onStepIndexChange) {
        this.props.onStepIndexChange(SyntheticEvent(newIndex));
      }
    };

    this.handleKeyDown = evt => {
      if (evt.key === 'ArrowRight') {
        this.handleClickNextStep();
      }

      if (evt.key === 'ArrowLeft') {
        this.handleClickPreviousStep();
      }
    };
  }

  componentDidMount() {
    const {
      steps
    } = this.props;
    preloadImages(steps);
  }

  componentDidUpdate() {
    const {
      steps
    } = this.props;
    preloadImages(steps);
  }

  renderCount(steps, computedStepIndex) {
    const totalSteps = steps ? steps.length : 0;
    const currentStepDisplayNumber = computedStepIndex + 1;
    return /*#__PURE__*/_jsxs(CarouselCurrent, {
      use: "help",
      children: [/*#__PURE__*/_jsx("span", {
        className: "sr-only",
        children: I18n.text('ui.UICarousel.current', {
          currentStepDisplayNumber,
          totalSteps
        })
      }), /*#__PURE__*/_jsx("span", {
        "aria-hidden": true,
        children: `${currentStepDisplayNumber}/${totalSteps}`
      })]
    });
  }

  renderFooter(allowStepLoop, use, steps, computedStepIndex) {
    const totalSteps = steps ? steps.length : 0;
    const renderedCount = allowStepLoop ? null : this.renderCount(steps, computedStepIndex);
    let renderedFooter;
    renderedFooter = renderedCount;

    if (use === 'max') {
      renderedFooter = totalSteps > MAX_THUMBNAILS ? renderedCount : this.renderThumbnails(steps, computedStepIndex);
    }

    return renderedFooter && /*#__PURE__*/_jsx(CarouselFooter, {
      use: use,
      children: renderedFooter
    });
  }

  renderThumbnails(steps, computedStepIndex) {
    const totalThumbs = steps;
    const thumbs = totalThumbs ? totalThumbs.map((step, i) => {
      if (!step) return null;
      if (!step.image) return null;
      const isCurrentStep = computedStepIndex === i || undefined;
      return /*#__PURE__*/_jsx(CarouselThumbnail, {
        "aria-current": isCurrentStep,
        onClick: partial(this.handleChange, i),
        children: /*#__PURE__*/_jsx(AspectRatioBox, {
          children: /*#__PURE__*/_jsx(CarouselThumbnailImage, {
            alt: step.alt,
            src: step.image
          })
        })
      }, i);
    }) : null;
    return /*#__PURE__*/_jsx(UIButtonWrapper, {
      alignItems: "start",
      horizontalGap: THUMBNAIL_GAP,
      children: thumbs
    });
  }

  render() {
    const _this$props = this.props,
          {
      allowStepLoop,
      Heading = CarouselTitle,
      id,
      imageHeight,
      MainContent = CarouselContentInner,
      maxWidth,
      stepIndex,
      steps,
      titleText,
      use
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["allowStepLoop", "Heading", "id", "imageHeight", "MainContent", "maxWidth", "onStepIndexChange", "stepIndex", "steps", "titleText", "use"]);

    const computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
    this._id = this._id || uniqueId('carousel');
    const computedId = id || this._id;
    const describedById = `${computedId}-desc`;
    const currentStep = steps ? steps[computedStepIndex] : null;
    const isFirstStep = computedStepIndex === 0;
    const isLastStep = computedStepIndex === getMaxIndex(steps);
    return /*#__PURE__*/_jsxs(CarouselWrapper, Object.assign({}, rest, {
      "aria-atomic": true,
      "aria-describedby": describedById,
      "aria-live": "polite",
      id: computedId,
      onKeyDown: this.handleKeyDown,
      maxWidth: maxWidth,
      tabIndex: 0,
      use: use,
      children: [/*#__PURE__*/_jsxs(CarouselHeader, {
        use: use,
        children: [titleText && /*#__PURE__*/_jsx(Heading, {
          children: titleText
        }), /*#__PURE__*/_jsx("div", {
          "aria-hidden": true,
          className: "sr-only",
          id: describedById,
          children: I18n.text('ui.UICarousel.tip')
        })]
      }), /*#__PURE__*/_jsx(CarouselPrevious, {
        children: /*#__PURE__*/_jsx(UIPreviousButton, {
          "aria-controls": computedId,
          "aria-hidden": true,
          "aria-label": I18n.text('ui.UICarousel.prev'),
          onClick: this.handleClickPreviousStep,
          disabled: isFirstStep && !allowStepLoop,
          size: "sm"
        })
      }), /*#__PURE__*/_jsx(CarouselContent, {
        use: use,
        children: currentStep && /*#__PURE__*/_jsx(MainContent, {
          children: currentStep.image ? /*#__PURE__*/_jsx(CarouselImage, {
            alt: currentStep.alt,
            objectFit: "contain",
            shape: "rounded",
            src: currentStep.image,
            style: {
              height: imageHeight
            }
          }) : currentStep.content
        })
      }), /*#__PURE__*/_jsx(CarouselNext, {
        children: /*#__PURE__*/_jsx(UINextButton, {
          "aria-controls": computedId,
          "aria-hidden": true,
          "aria-label": I18n.text('ui.UICarousel.next'),
          onClick: this.handleClickNextStep,
          disabled: isLastStep && !allowStepLoop,
          size: "sm"
        })
      }), currentStep && currentStep.description && /*#__PURE__*/_jsxs(CarouselCallout, {
        use: use,
        children: [currentStep.name && /*#__PURE__*/_jsx(CarouselSubtitle, {
          role: "presentation",
          children: currentStep.name
        }), currentStep.description]
      }), this.renderFooter(allowStepLoop, use, steps, computedStepIndex)]
    }));
  }

}

const propTypes = {
  allowStepLoop: PropTypes.bool.isRequired,
  defaultStepIndex: PropTypes.number,
  Heading: getComponentPropType(H4),
  imageHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  MainContent: PropTypes.elementType,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onStepIndexChange: PropTypes.func,
  steps: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.node,
    content: PropTypes.node,
    image: PropTypes.string
  })),
  stepIndex: PropTypes.number,
  titleText: PropTypes.node,
  use: PropTypes.oneOf(USE_OPTIONS)
};
UICarousel.propTypes = propTypes;
UICarousel.defaultProps = {
  allowStepLoop: false,
  MainContent: CarouselContentInner,
  maxWidth: DEFAULT_MAX_WIDTH,
  Heading: CarouselTitle,
  stepIndex: 0,
  use: 'lite'
};
UICarousel.displayName = 'UICarousel';
export default Controllable(UICarousel, ['stepIndex']);